import React from 'react';
import { colors, effects, styles, typography } from '../theme';
import ObservationPanel from './ObservationPanel';

// P6 — Persistent Surface. The Live Preview Card is always visible in the
// customize flow; never tucked behind a tab or a click.
//
// Phase 2 ships static stats sourced from the archetype's `previewStats`
// block. The full derived-stats engine (calculateDerivedStats, applyClass,
// etc.) lands in a follow-up — but the UX shape needs the persistent surface
// from day one so we can iterate on it.

const STAT_ABBREV = {
  str: 'STR',
  dex: 'DEX',
  con: 'CON',
  int: 'INT',
  wis: 'WIS',
  cha: 'CHA',
};

const abilityMod = (score) => Math.floor((score - 10) / 2);

const formatMod = (mod) => (mod >= 0 ? `+${mod}` : `${mod}`);

const StatRow = ({ label, value, hint }) => (
  <div
    style={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      padding: '4px 0',
      borderBottom: `1px solid ${colors.borderLight}`,
    }}
    title={hint || undefined}
  >
    <span style={{ color: colors.textSecondary, fontSize: 13 }}>{label}</span>
    <strong style={{ color: colors.textPrimary, fontSize: 15 }}>{value}</strong>
  </div>
);

const AbilityCell = ({ ability, score }) => {
  const mod = abilityMod(score);
  // P5: every number is a receipt. Phase 2 ships hover-tooltip surface; the
  // decomposition is currently trivial (score → mod). When the derived-stats
  // engine lands we'll expand this into "Base 10 + Race +2 + ASI +1 = 13".
  const receipt = `${STAT_ABBREV[ability]} ${score} → modifier ${formatMod(mod)}`;
  return (
    <div
      data-testid={`ability-${ability}`}
      title={receipt}
      style={{
        textAlign: 'center',
        padding: '10px 8px',
        background: colors.bgInput,
        borderRadius: effects.borderRadiusSm,
        border: `1px solid ${colors.borderLight}`,
        cursor: 'help',
      }}
    >
      <div
        style={{
          fontSize: 10,
          color: colors.textMuted,
          letterSpacing: 1,
          textTransform: 'uppercase',
          marginBottom: 2,
        }}
      >
        {STAT_ABBREV[ability]}
      </div>
      <div style={{ fontSize: 18, fontWeight: 'bold', color: colors.textPrimary }}>
        {score}
      </div>
      <div style={{ fontSize: 12, color: colors.textSecondary, marginTop: 2 }}>
        {formatMod(mod)}
      </div>
    </div>
  );
};

const FeatureLine = ({ feature }) => (
  <li
    style={{
      marginBottom: 8,
      paddingBottom: 8,
      borderBottom: `1px solid ${colors.borderLight}`,
      listStyle: 'none',
    }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <strong style={{ color: colors.textPrimary, fontSize: 13 }}>{feature.name}</strong>
      <span style={{ fontSize: 11, color: colors.textMuted, fontStyle: 'italic' }}>
        L{feature.level}
      </span>
    </div>
    {feature.summary ? (
      <div
        style={{
          fontSize: 12,
          color: colors.textSecondary,
          marginTop: 4,
          lineHeight: 1.4,
        }}
      >
        {feature.summary}
      </div>
    ) : null}
  </li>
);

// `customName` overrides archetype.name in the preview header.
// `preview` is the engine output from `buildPreview()` (Phase 2.5+). When
// present, derived stats and ability scores come from the engine and reflect
// any customizations the user has made. When absent we fall back to the
// archetype's static `previewStats` (gallery thumbnails, tests that don't
// run the engine, etc.).
const LivePreviewCard = ({ archetype, customName, preview = null }) => {
  if (!archetype) {
    return (
      <aside
        data-testid="live-preview-card"
        style={{
          ...styles.card,
          minHeight: 480,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: colors.textMuted,
          fontStyle: 'italic',
        }}
      >
        Pick an archetype to preview your character.
      </aside>
    );
  }

  const displayName = customName || archetype.name;
  const abilities = preview?.abilities || archetype.picks?.abilities || {};
  const derived = preview?.derived || null;
  const stats = derived
    ? {
        ac: derived.ac,
        hp: derived.hp,
        speed: derived.speed,
        initiative: derived.initiative,
        proficiencyBonus: derived.proficiencyBonus,
        spellSaveDc: derived.spell?.saveDc,
        spellAttackBonus: derived.spell?.attackBonus,
      }
    : archetype.previewStats || {};
  const features = archetype.featurePreviews || [];

  // Compose hover-receipts from engine breakdowns when available, else use
  // the static hint text we ship by default.
  const breakdownToTitle = (rows) =>
    rows && rows.length
      ? rows.map((r) => `${r.source}: ${r.value >= 0 ? '+' : ''}${r.value}`).join('  ·  ')
      : null;
  const acHint = derived
    ? `AC ${derived.ac} = ${breakdownToTitle(derived.acBreakdown)}`
    : 'Base AC from armor + Dex (capped) + bonuses';
  const hpHint = derived
    ? `HP ${derived.hp} = ${breakdownToTitle(derived.hpBreakdown)}`
    : 'Max HP at level 1 (hit die + Con mod)';
  const initHint = derived
    ? breakdownToTitle(derived.initiativeBreakdown)
    : 'Equals your Dex modifier';
  const speedHint = derived
    ? breakdownToTitle(derived.speedBreakdown)
    : 'Species base speed (Phase 3 adds movement modes)';
  const pbHint = derived
    ? breakdownToTitle(derived.pbBreakdown)
    : 'From character level (L1-4 = +2, L5-8 = +3, …)';

  // P7: Surface unresolved content as a visible fact, not a silent fallback.
  // If `preview.missing` is populated, the engine couldn't find one of the
  // referenced JSONs (e.g. starter-kit references an unauthored item).
  // The displayed stats are still computed from defaults, so we tag them
  // honestly rather than show a confidently-wrong sheet.
  const missingPills = preview?.missing || [];

  // Phase 2.6: combat-fluency sections (weapon attacks, skills, passive
  // perception, hit dice). All four come straight off the engine when
  // present; nothing renders when the engine isn't run (Phase 2 fallback).
  const weaponAttacks = derived?.weaponAttacks || [];
  const skillRows = derived?.skills || [];
  const proficientSkills = skillRows.filter((s) => s.proficient);
  const passivePerception = derived?.passivePerception || null;
  const hitDice = derived?.hitDice || null;

  return (
    <aside
      data-testid="live-preview-card"
      style={{
        ...styles.card,
        padding: 20,
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Identity header */}
      <header style={{ marginBottom: 16 }}>
        <div
          style={{
            width: 88,
            height: 88,
            margin: '0 auto 12px',
            borderRadius: '50%',
            background: colors.bgInput,
            border: `2px solid ${colors.borderAccent}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colors.textMuted,
            fontFamily: typography.headingFamily,
            fontSize: 32,
          }}
          aria-label="Portrait placeholder"
        >
          {displayName.slice(0, 1).toUpperCase()}
        </div>
        <h3
          style={{
            ...styles.heading,
            fontSize: 20,
            textAlign: 'center',
            marginBottom: 4,
          }}
        >
          {displayName}
        </h3>
        <div
          style={{
            fontSize: 12,
            color: colors.textSecondary,
            textAlign: 'center',
            fontStyle: 'italic',
          }}
        >
          {archetype.tagline}
        </div>
      </header>

      {/* Derived stats. Every number gets a `title` hover-receipt per P5.
          When the engine is wired (preview prop), receipts list every
          contributing source. Otherwise we fall back to one-line hints.
          Hit Dice sits next to HP so the recovery-pool relationship reads
          at a glance. */}
      <div style={{ marginBottom: 16 }}>
        <StatRow label="Armor Class" value={stats.ac ?? '—'} hint={acHint} />
        <StatRow label="Hit Points" value={stats.hp ?? '—'} hint={hpHint} />
        {hitDice ? (
          <StatRow
            label="Hit Dice"
            value={hitDice.display}
            hint="Spend during a Short Rest to roll + Con modifier and recover HP. Resets to half on Long Rest."
          />
        ) : null}
        <StatRow label="Speed" value={`${stats.speed ?? 30} ft`} hint={speedHint} />
        <StatRow
          label="Initiative"
          value={formatMod(stats.initiative ?? 0)}
          hint={initHint}
        />
        <StatRow
          label="Proficiency Bonus"
          value={`+${stats.proficiencyBonus ?? 2}`}
          hint={pbHint}
        />
        {stats.spellSaveDc ? (
          <StatRow
            label="Spell Save DC"
            value={stats.spellSaveDc}
            hint={
              derived?.spell
                ? breakdownToTitle(derived.spell.breakdown.saveDc)
                : '8 + Proficiency Bonus + spellcasting ability modifier'
            }
          />
        ) : null}
        {stats.spellAttackBonus !== undefined ? (
          <StatRow
            label="Spell Attack"
            value={formatMod(stats.spellAttackBonus)}
            hint={
              derived?.spell
                ? breakdownToTitle(derived.spell.breakdown.attackBonus)
                : 'Proficiency Bonus + spellcasting ability modifier'
            }
          />
        ) : null}
      </div>

      {/* Ability score grid */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6,
          marginBottom: 16,
        }}
        data-testid="ability-grid"
      >
        {Object.keys(STAT_ABBREV).map((ability) => (
          <AbilityCell
            key={ability}
            ability={ability}
            score={abilities[ability] ?? 10}
          />
        ))}
      </div>

      {/* Passive Perception. */}
      {passivePerception ? (
        <StatRow
          label="Passive Perception"
          value={passivePerception.total}
          hint={breakdownToTitle(passivePerception.breakdown)}
        />
      ) : null}

      {/* Weapon attacks. */}
      {weaponAttacks.length ? (
        <div style={{ marginBottom: 16 }} data-testid="weapon-attacks">
          <h4
            style={{
              margin: '12px 0 8px 0',
              fontSize: 12,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 'normal',
            }}
          >
            Attacks
          </h4>
          {weaponAttacks.map((attack) => (
            <div
              key={attack.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'baseline',
                padding: '6px 0',
                borderBottom: `1px solid ${colors.borderLight}`,
                fontSize: 13,
              }}
              title={`Attack ${formatMod(attack.attackBonus)} = ${breakdownToTitle(attack.attackBreakdown)}\nDamage ${attack.damage.display}`}
            >
              <span style={{ color: colors.textPrimary }}>
                {attack.name}
                <span
                  style={{
                    color: colors.textMuted,
                    marginLeft: 6,
                    fontSize: 11,
                    fontStyle: 'italic',
                  }}
                >
                  {attack.kind === 'ranged' ? 'ranged' : 'melee'}
                </span>
              </span>
              <span style={{ color: colors.textPrimary, fontFamily: 'monospace' }}>
                {formatMod(attack.attackBonus)} · {attack.damage.display}
              </span>
            </div>
          ))}
        </div>
      ) : null}

      {/* Proficient skills only. The full skill list ships under a future
          "Show all skills" affordance; the preview keeps to what the
          character is actually trained in (P12 friction reduction). */}
      {proficientSkills.length ? (
        <div style={{ marginBottom: 16 }} data-testid="proficient-skills">
          <h4
            style={{
              margin: '12px 0 8px 0',
              fontSize: 12,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 'normal',
            }}
          >
            Proficient Skills
          </h4>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            {proficientSkills.map((skill) => (
              <span
                key={skill.id}
                title={breakdownToTitle(skill.breakdown)}
                style={{
                  fontSize: 12,
                  padding: '3px 9px',
                  borderRadius: 4,
                  background: colors.bgInput,
                  border: `1px solid ${colors.borderLight}`,
                  color: colors.textPrimary,
                  cursor: 'help',
                }}
              >
                {skill.name} {formatMod(skill.total)}
              </span>
            ))}
          </div>
        </div>
      ) : null}

      {/* Feature foreshadow */}
      {features.length ? (
        <div>
          <h4
            style={{
              margin: '0 0 8px 0',
              fontSize: 12,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: colors.textSecondary,
              fontWeight: 'normal',
            }}
          >
            What you can do
          </h4>
          <ul style={{ margin: 0, padding: 0 }} data-testid="feature-list">
            {features.map((feature) => (
              <FeatureLine key={`${feature.level}-${feature.name}`} feature={feature} />
            ))}
          </ul>
        </div>
      ) : null}

      {/* Missing-content surface (P7). When the engine couldn't resolve a
          referenced JSON, surface it as a visible pill list rather than
          letting the displayed stats look authoritative. */}
      {missingPills.length ? (
        <div
          data-testid="preview-missing-content"
          style={{
            marginTop: 12,
            padding: 10,
            borderLeft: `3px solid ${colors.warning || colors.accent}`,
            background: colors.warningBg,
            borderRadius: 4,
          }}
        >
          <div
            style={{
              fontSize: 11,
              letterSpacing: 1,
              textTransform: 'uppercase',
              color: colors.textSecondary,
              marginBottom: 6,
            }}
          >
            Missing content
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
            {missingPills.map((m) => (
              <span
                key={m}
                style={{
                  fontSize: 11,
                  padding: '2px 8px',
                  borderRadius: 4,
                  background: colors.bgInput,
                  border: `1px solid ${colors.borderLight}`,
                  color: colors.textMuted,
                  fontFamily: 'monospace',
                }}
              >
                {m}
              </span>
            ))}
          </div>
          <div style={{ fontSize: 11, color: colors.textMuted, marginTop: 6, fontStyle: 'italic' }}>
            Displayed stats use defaults for unresolved items.
          </div>
        </div>
      ) : null}

      {/* Observations */}
      <ObservationPanel observations={archetype.observations || []} />
    </aside>
  );
};

export default LivePreviewCard;
